import type {VideosManifestEntry} from '@/types/videosManifest';
import {getRouteSegments} from '@/config/routeSegments';

function humanizeSegment(segment: string): string {
    const spaced = segment
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/[-_]+/g, ' ')
        .trim();
    return spaced
        .split(/\s+/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function getPageKeyFromPathname(pathname: string): string {
    const [first] = getRouteSegments(pathname);
    return (first ?? 'home').toLowerCase();
}

/** Human-readable label for the current top-level page (e.g. "Application Editor"). */
export function getPageLabelFromPathname(pathname: string): string {
    const [first] = getRouteSegments(pathname);
    return humanizeSegment(first ?? 'home');
}

export function getPageKeyFromFile(file: string): string {
    const stem = file.split('/').pop() ?? file;
    const dash = stem.indexOf('-');
    if (dash === -1) return stem.replace(/\.[^.]+$/, '').toLowerCase();
    return stem.slice(0, dash).toLowerCase();
}

/** Human-readable label for a video, derived from its filename prefix. */
export function getVideoPageLabel(entry: VideosManifestEntry): string {
    const stem = entry.file.split('/').pop() ?? entry.file;
    const dash = stem.indexOf('-');
    const prefix = dash === -1 ? stem.replace(/\.[^.]+$/, '') : stem.slice(0, dash);
    return humanizeSegment(prefix);
}

/**
 * Find the manifest video scoped to the current page.
 *
 * Matching walks the route from the deepest segment upward so a video scoped to
 * a nested page wins over one scoped to its parent area. The home page has no
 * feature segments, so it matches a `home-*` video.
 */
export function findPageVideo(entries: VideosManifestEntry[], pathname: string): VideosManifestEntry | null {
    const segments = getRouteSegments(pathname).map(segment => segment.toLowerCase());
    const keys = segments.length > 0 ? segments : ['home'];
    for (let i = keys.length - 1; i >= 0; i -= 1) {
        const match = entries.find(entry => getPageKeyFromFile(entry.file) === keys[i]);
        if (match) {
            return match;
        }
    }
    return null;
}

export function buildVideoAssetUrl(file: string): string {
    return `/videos/${file}`;
}
